import React from 'react';
import { intentTypes, getIntentStyle } from '@/shared/utils/constants';
import ComingSoonCell from './ComingSoonCell';

interface CellRendererParams {
  value: string[] | null | undefined;
  context?: {
    language?: 'fa' | 'en';
    isDark?: boolean;
  };
}

const IntentCellRenderer = (params: CellRendererParams) => {
  const value = params.value; // Array of intent strings
  const language = params.context?.language || 'fa';
  const isDark = params.context?.isDark || document.documentElement.classList.contains('dark');
  
  if (!value || !Array.isArray(value) || value.length === 0) {
    return <ComingSoonCell language={language} isDark={isDark} />;
  }

  // Generate tooltip HTML for each intent
  const generateIntentTooltipHTML = (intentType: string): string => {
    const intent = intentTypes[intentType];
    if (!intent) return '';
    
    const titleText = language === 'fa' ? `نیت: ${intent.label}` : `Intent: ${intent.enLabel}`;
    const bgColor = isDark ? '#ffffff' : 'rgb(31, 41, 55)';
    const textColor = isDark ? 'rgb(31, 41, 55)' : 'white';
    const borderColor = isDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
    const descriptionColor = isDark ? 'rgb(75, 85, 99)' : 'rgb(209, 213, 219)';
    
    return `
      <div style="background-color: ${bgColor}; border-radius: 8px; padding: 16px; min-width: 300px; max-width: 400px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04); word-wrap: break-word; overflow-wrap: break-word;">
        <div style="color: ${textColor}; font-weight: 600; font-size: 14px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid ${borderColor}; word-wrap: break-word; overflow-wrap: break-word;">
          ${titleText}
        </div>
        <div style="color: ${descriptionColor}; font-size: 13px; line-height: 1.6; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
          ${language === 'fa' ? intent.description.fa : intent.description.en}
        </div>
      </div>
    `;
  };

  // Update tooltip HTML on mouse enter
  const handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>, intentType: string) => {
    const currentIsDark = document.documentElement.classList.contains('dark');
    const intent = intentTypes[intentType];
    if (!intent) return;
    
    const titleText = language === 'fa' ? `نیت: ${intent.label}` : `Intent: ${intent.enLabel}`;
    const bgColor = currentIsDark ? '#ffffff' : 'rgb(31, 41, 55)';
    const textColor = currentIsDark ? 'rgb(31, 41, 55)' : 'white';
    const borderColor = currentIsDark ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)';
    const descriptionColor = currentIsDark ? 'rgb(75, 85, 99)' : 'rgb(209, 213, 219)';
    
    const updatedTooltipHTML = `
      <div style="background-color: ${bgColor}; border-radius: 8px; padding: 16px; min-width: 300px; max-width: 400px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04); word-wrap: break-word; overflow-wrap: break-word;">
        <div style="color: ${textColor}; font-weight: 600; font-size: 14px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid ${borderColor}; word-wrap: break-word; overflow-wrap: break-word;">
          ${titleText}
        </div>
        <div style="color: ${descriptionColor}; font-size: 13px; line-height: 1.6; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
          ${language === 'fa' ? intent.description.fa : intent.description.en}
        </div>
      </div>
    `;
    
    e.currentTarget.setAttribute('data-tooltip-html', updatedTooltipHTML);
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}>
      {value.map((intentType, index) => {
        const intent = getIntentStyle(intentType);
        if (!intent) return null;
        
        const tooltipHTML = generateIntentTooltipHTML(intentType);
        const displayText = language === 'fa' ? intent.abbreviation : intent.enAbbreviation;
        
        return (
          <span
            key={`${intentType}-${index}`}
            data-tooltip-id="intent-tooltip-global"
            data-tooltip-place="top"
            data-tooltip-html={tooltipHTML}
            onMouseEnter={(e) => handleMouseEnter(e, intentType)}
            style={{
              backgroundColor: intent.bgColor,
              color: intent.color,
              padding: '4px 10px',
              borderRadius: '4px',
              fontWeight: '600',
              fontSize: '12px',
              display: 'inline-block',
              textAlign: 'center',
              lineHeight: '1.4',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {displayText}
          </span>
        );
      })}
    </div>
  );
};

export default IntentCellRenderer;


